import { useCallback, useEffect, useRef, useState } from "react"
import { useAppMessage, useDaily, useLocalSessionId } from "./callTransport"
import {
  MAX_ACTIVE_REACTIONS,
  REACTION_DISPLAY_MS,
  REACTION_MESSAGE_TYPE,
  createSignalId,
  isReactionEmoji,
  participantDisplayName,
  type ReactionAppMessage,
} from "./callSignals"

export type ActiveReaction = {
  id: string
  /** One complete Unicode emoji, including any modifiers or ZWJ sequence. */
  emoji: string
  sessionId: string
  name: string
  local: boolean
  at: number
}

/**
 * Emoji reactions like Prebuilt's React menu: broadcast over an app message, shown floating
 * over the stage for a few seconds on every side, and announced for screen readers.
 */
export function useReactions({ enabled }: { enabled: boolean }) {
  const daily = useDaily()
  const localSessionId = useLocalSessionId()
  const [reactions, setReactions] = useState<ActiveReaction[]>([])
  const [announcement, setAnnouncement] = useState<string | null>(null)
  const timersRef = useRef<Map<string, number>>(new Map())

  const push = useCallback((reaction: ActiveReaction) => {
    setReactions((current) => [...current, reaction].slice(-MAX_ACTIVE_REACTIONS))
    setAnnouncement(`${reaction.local ? "You" : reaction.name} reacted with ${reaction.emoji}`)
    const timer = window.setTimeout(() => {
      timersRef.current.delete(reaction.id)
      setReactions((current) => current.filter((item) => item.id !== reaction.id))
    }, REACTION_DISPLAY_MS)
    timersRef.current.set(reaction.id, timer)
  }, [])

  useEffect(() => () => {
    timersRef.current.forEach((timer) => window.clearTimeout(timer))
    timersRef.current.clear()
  }, [])

  const enabledRef = useRef(enabled)
  enabledRef.current = enabled
  const handleAppMessage = useCallback((event: { data?: ReactionAppMessage; fromId?: string }) => {
    const data = event?.data
    if (!enabledRef.current || data?.type !== REACTION_MESSAGE_TYPE || !isReactionEmoji(data.emoji)) return
    const sessionId = event.fromId || "unknown"
    push({
      id: typeof data.id === "string" && data.id ? data.id : createSignalId(),
      emoji: data.emoji,
      sessionId,
      name: participantDisplayName(daily, event.fromId, "Someone"),
      local: false,
      at: Date.now(),
    })
  }, [daily, push])
  const sendAppMessage = useAppMessage<ReactionAppMessage>({ onAppMessage: handleAppMessage })

  const sendReaction = useCallback((emoji: string) => {
    if (!enabled || !isReactionEmoji(emoji)) return
    const id = createSignalId()
    sendAppMessage({ type: REACTION_MESSAGE_TYPE, id, emoji }, "*")
    push({ id, emoji, sessionId: localSessionId, name: "You", local: true, at: Date.now() })
  }, [enabled, localSessionId, push, sendAppMessage])

  return { reactions, sendReaction, announcement }
}
